import type { ReactElement } from 'react';
import { MedicareApiPlan } from 'types/api';
import CheckIcon from './icons/CheckIcon';
import XIcon from './icons/XIcon';
import Tooltip from './Tooltip';
import { PART_D_DRUG_OOPM } from 'lib/CMSData';
import styles from './PlanCard.module.scss';
import { formatPlanDetails } from 'utils/planFormatting';
import { formatCurrency } from 'utils/formatters';

interface PlanCardDetailsProps {
  plan: MedicareApiPlan;
}

// Table row configuration - defines what to show and how
type TableRowConfig = {
  label: string;
  value: string | ReactElement;
  show: boolean;
  tooltip?: string;
};

export default function PlanCardDetails({ plan }: PlanCardDetailsProps) {
  const formatted = formatPlanDetails(plan);

  const tableRows: TableRowConfig[] = [
    {
      label: 'Monthly premium',
      value: formatted.monthlyPremium,
      show: true,
      tooltip: 'The amount you pay to be a member of the plan.',
    },
    {
      label: 'Medical deductible',
      value: formatted.deductible,
      show: plan.type === 'ma' || plan.type === 'mapd',
      tooltip:
        'The amount you have to pay for medical services before the plan starts to pay. This could be in addition to the Medicare Supplement plan deductible, if it has one',
    },
    {
      label: 'Drug deductible',
      value: formatted.drugDeductible,
      show: plan.type !== 'gap',
      tooltip:
        'The amount you have to pay for prescriptions before the plan starts to pay.',
    },
    {
      label: 'Drug out-of-pocket max',
      value: formatCurrency(PART_D_DRUG_OOPM[2026]),
      show: plan.type === 'pdp',
      tooltip:
        'The most you can pay (including the deductible), before a plan covers all costs for drugs in its formulary. It does not include the premium, if there is one.',
    },
    {
      label: 'Out-of-pocket max',
      value: formatted.oopm,
      show: plan.type === 'ma' || plan.type === 'mapd',
      tooltip:
        'The most you can pay (including the deductible), before a plan covers all costs. It does not include the premium, if there is one, and it might not apply to all services.',
    },
    {
      label: 'Medical coverage',
      value: <XIcon />,
      show: plan.type === 'pdp',
    },
    {
      label: 'See any provider',
      value: <CheckIcon />,
      show: plan.type === 'gap',
    },
    {
      label: 'Prescription drug coverage',
      value: plan.type !== 'gap' ? <CheckIcon /> : <XIcon />,
      show: true,
    },

    {
      label: 'Dental coverage',
      value: plan.hasDental ? <CheckIcon /> : <XIcon />,
      show: plan.type !== 'gap' && plan.type !== 'pdp',
    },
    {
      label: 'Vision coverage',
      value: plan.hasVision ? <CheckIcon /> : <XIcon />,
      show: plan.type !== 'gap' && plan.type !== 'pdp',
    },
  ];

  return (
    <div className={styles.planBasicDetails}>
      <table>
        <caption>Basic Plan Details</caption>
        <tbody>
          {tableRows
            .filter((row) => row.show)
            .map((row) => (
              <tr key={row.label}>
                <th scope="row">
                  <span className={styles.labelRow}>
                    <span>{row.label}</span>
                    {row.tooltip && (
                      <Tooltip horizontalAlign="right" subject={row.label}>
                        {row.tooltip}
                      </Tooltip>
                    )}
                  </span>
                </th>
                <td>{row.value}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
